/**
 * Video utilities for work order video uploads
 * Handles compression, thumbnail generation, and validation
 */

// Video configuration constants
export const VIDEO_CONFIG = {
  maxDuration: 60,              // 1 minute max
  maxInputSize: 500 * 1024 * 1024, // 500MB max input
  maxVideosPerWorkOrder: 2,
  expirationDays: 60,           // Videos expire after 60 days
  
  // Compression targets
  resolution: 720,              // 720p
  videoBitrate: '1500k',        // 1.5 Mbps
  audioBitrate: '128k',
  
  // Thumbnail settings
  thumbnailWidth: 320,
  thumbnailHeight: 180,
  thumbnailQuality: 0.8,
  
  // Accepted formats
  acceptedFormats: ['video/mp4', 'video/quicktime', 'video/webm', 'video/x-m4v'],
  acceptedExtensions: ['.mp4', '.mov', '.webm', '.m4v'],
};

/**
 * Check if a file is a valid video format
 */
export const isValidVideoFormat = (file) => {
  if (!file) return false;
  
  const extension = '.' + file.name.split('.').pop()?.toLowerCase();
  const isValidMime = VIDEO_CONFIG.acceptedFormats.includes(file.type) || 
                      file.type.startsWith('video/');
  const isValidExtension = VIDEO_CONFIG.acceptedExtensions.includes(extension);
  
  return isValidMime || isValidExtension;
};

/**
 * Get video duration in seconds
 */
export const getVideoDuration = (file) => {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    
    video.onloadedmetadata = () => {
      window.URL.revokeObjectURL(video.src);
      resolve(Math.round(video.duration));
    };
    
    video.onerror = () => {
      window.URL.revokeObjectURL(video.src);
      reject(new Error('Failed to load video metadata'));
    };
    
    video.src = URL.createObjectURL(file);
  });
};

/**
 * Validate video file
 * Returns { valid: boolean, error?: string }
 */
export const validateVideo = async (file) => {
  // Check format
  if (!isValidVideoFormat(file)) {
    return {
      valid: false,
      error: 'Invalid video format. Please use MP4, MOV, or WebM.',
    };
  }
  
  // Check input file size
  if (file.size > VIDEO_CONFIG.maxInputSize) {
    const sizeMB = Math.round(VIDEO_CONFIG.maxInputSize / (1024 * 1024));
    return {
      valid: false,
      error: `Video file is too large. Maximum size is ${sizeMB}MB.`,
    };
  }
  
  // Check duration
  try {
    const duration = await getVideoDuration(file);
    if (duration > VIDEO_CONFIG.maxDuration) {
      return {
        valid: false,
        error: `Video is too long. Maximum duration is ${VIDEO_CONFIG.maxDuration} seconds.`,
      };
    }
    
    return { valid: true, duration };
  } catch (error) {
    return {
      valid: false,
      error: 'Could not read video file. Please try a different video.',
    };
  }
};

/**
 * Generate thumbnail from video at specified time
 */
export const generateThumbnail = (file, timeSeconds = 0) => {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.muted = true;
    video.playsInline = true;
    
    video.onloadeddata = () => {
      // Seek to the specified time (or 0 for first frame)
      video.currentTime = Math.min(timeSeconds, video.duration);
    };
    
    video.onseeked = () => {
      try {
        const canvas = document.createElement('canvas');
        const aspectRatio = video.videoWidth / video.videoHeight;
        
        // Calculate dimensions maintaining aspect ratio
        let width = VIDEO_CONFIG.thumbnailWidth;
        let height = VIDEO_CONFIG.thumbnailHeight;
        
        if (aspectRatio > width / height) {
          height = Math.round(width / aspectRatio);
        } else {
          width = Math.round(height * aspectRatio);
        }
        
        canvas.width = width;
        canvas.height = height;
        
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0, width, height);
        
        canvas.toBlob(
          (blob) => {
            window.URL.revokeObjectURL(video.src);
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Failed to generate thumbnail'));
            }
          },
          'image/jpeg',
          VIDEO_CONFIG.thumbnailQuality
        );
      } catch (error) {
        window.URL.revokeObjectURL(video.src);
        reject(error);
      }
    };
    
    video.onerror = () => {
      window.URL.revokeObjectURL(video.src);
      reject(new Error('Failed to load video for thumbnail'));
    };
    
    video.src = URL.createObjectURL(file);
  });
};

/**
 * Check if FFmpeg.wasm is supported in this browser
 * Requires SharedArrayBuffer which needs specific headers
 */
export const isFFmpegSupported = () => {
  return typeof SharedArrayBuffer !== 'undefined';
};

/**
 * Compress video using FFmpeg.wasm
 * Returns compressed video blob
 * Falls back to original file if compression fails or is not supported
 */
export const compressVideo = async (file, onProgress) => {
  // Check if compression is supported
  if (!isFFmpegSupported()) {
    onProgress?.(100);
    return { blob: file, wasCompressed: false };
  }

  try {
    // Dynamic import to avoid loading FFmpeg until needed
    const { FFmpeg } = await import('@ffmpeg/ffmpeg');
    const { fetchFile, toBlobURL } = await import('@ffmpeg/util');
    
    const ffmpeg = new FFmpeg();
    
    // Set up progress handler
    ffmpeg.on('progress', ({ progress }) => {
      if (onProgress) {
        onProgress(Math.round(progress * 100));
      }
    });
    
    // Load FFmpeg with CORS-enabled URLs (UMD build for broader compatibility)
    const baseURL = 'https://unpkg.com/@ffmpeg/core@0.12.6/dist/umd';
    await ffmpeg.load({
      coreURL: await toBlobURL(`${baseURL}/ffmpeg-core.js`, 'text/javascript'),
      wasmURL: await toBlobURL(`${baseURL}/ffmpeg-core.wasm`, 'application/wasm'),
    });
    
    // Write input file
    const inputFileName = 'input' + getExtension(file.name);
    const outputFileName = 'output.mp4';
    
    await ffmpeg.writeFile(inputFileName, await fetchFile(file));
    
    // Compress video
    // -vf scale: scale to 720p maintaining aspect ratio
    // -c:v libx264: use H.264 codec
    // -crf 28: constant rate factor (18-28 is good, higher = more compression)
    // -preset fast: encoding speed preset
    // -c:a aac: audio codec
    // -b:a 128k: audio bitrate
    // -movflags +faststart: optimize for web streaming
    await ffmpeg.exec([
      '-i', inputFileName,
      '-vf', `scale=-2:${VIDEO_CONFIG.resolution}`,
      '-c:v', 'libx264',
      '-crf', '28',
      '-preset', 'fast',
      '-c:a', 'aac',
      '-b:a', VIDEO_CONFIG.audioBitrate,
      '-movflags', '+faststart',
      '-y',
      outputFileName
    ]);
    
    // Read compressed file
    const data = await ffmpeg.readFile(outputFileName);
    
    // Clean up
    await ffmpeg.deleteFile(inputFileName);
    await ffmpeg.deleteFile(outputFileName);
    
    // Create blob from compressed data
    const blob = new Blob([data.buffer], { type: 'video/mp4' });
    
    return { blob, wasCompressed: true };
  } catch (error) {
    onProgress?.(100);
    // Return original file if compression fails
    return { blob: file, wasCompressed: false };
  }
};

/**
 * Get file extension
 */
const getExtension = (filename) => {
  const ext = filename.split('.').pop()?.toLowerCase();
  return ext ? `.${ext}` : '.mp4';
};

/**
 * Format file size for display
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Format duration for display (mm:ss)
 */
export const formatDuration = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

/**
 * Calculate expiration date (60 days from now)
 */
export const calculateExpirationDate = () => {
  const date = new Date();
  date.setDate(date.getDate() + VIDEO_CONFIG.expirationDays);
  return date.toISOString();
};

/**
 * Check if compression is needed based on file size and format
 */
export const needsCompression = (file) => {
  // Always compress if file is larger than 20MB
  if (file.size > 20 * 1024 * 1024) {
    return true;
  }
  
  // Compress non-MP4 files to ensure compatibility
  if (!file.type.includes('mp4')) {
    return true;
  }
  
  return false;
};

/**
 * Process video for upload
 * Validates, compresses if needed, generates thumbnail
 */
export const processVideoForUpload = async (file, onProgress) => {
  // Validate first
  const validation = await validateVideo(file);
  if (!validation.valid) {
    throw new Error(validation.error);
  }
  
  let videoBlob = file;
  let wasCompressed = false;
  
  // Compress if needed and supported
  if (needsCompression(file)) {
    onProgress?.({ stage: 'compressing', progress: 0 });
    const compressionResult = await compressVideo(file, (progress) => {
      onProgress?.({ stage: 'compressing', progress });
    });
    videoBlob = compressionResult.blob;
    wasCompressed = compressionResult.wasCompressed;
  }
  
  // Generate thumbnail
  onProgress?.({ stage: 'thumbnail', progress: 0 });
  const thumbnailBlob = await generateThumbnail(file, 1); // Get frame at 1 second
  onProgress?.({ stage: 'thumbnail', progress: 100 });
  
  return {
    videoBlob,
    thumbnailBlob,
    duration: validation.duration,
    wasCompressed,
    originalSize: file.size,
    compressedSize: videoBlob.size,
  };
};

